import { createAsyncThunk } from "@reduxjs/toolkit";
import NineSetupApiService, {
  toFriendlyNineSetupError,
} from "../apiServices/NineSetupApiService";
import {
  CreateNineSetupForm,
  NineSetupListFilters,
  UpdateNineSetupPayload,
  UpdateNineSetupEnquiryPayload,
  CalendarFilters,
  ReportRangeQuery,
  CalendarData,
} from "../../interfaces/NineSetupObject";

/**
 * Async thunks for the nine_setup pipeline.
 *
 * Every thunk rejects with friendly copy (not a raw errorCode) — unlike
 * fetchCaptainSummary, none of these has an error code that the slice needs to
 * treat as a *state* rather than a failure, so the mapping can happen here.
 *
 * `updateNineSetup` and `deleteNineSetup` take the setupId in their argument,
 * which the slice reads off `action.meta.arg` to key per-row loading state.
 * That coupling is deliberate: it is what keeps one row's spinner from
 * disabling every other row's buttons.
 */

/** Loads the caller's setups. The endpoint always returns everything the caller
 *  may see; `filters` narrows the result client-side inside the API service, so
 *  passing them saves no bandwidth — prefer filtering in the component and
 *  calling this once. */
export const fetchNineSetups = createAsyncThunk(
  "nineSetup/fetchNineSetups",
  async (filters: NineSetupListFilters | undefined, { rejectWithValue }) => {
    try {
      return await NineSetupApiService.getNineSetups(filters);
    } catch (error) {
      return rejectWithValue(toFriendlyNineSetupError(error));
    }
  }
);

/**
 * Creates a setup row. Not idempotent server-side — `customerMobile` is UNIQUE,
 * so a double-submit surfaces as DUPLICATE_CUSTOMER_MOBILE rather than quietly
 * returning the existing row (the opposite of createPendingBusiness). Callers
 * MUST block the submit button while this is in flight.
 */
export const createNineSetup = createAsyncThunk(
  "nineSetup/createNineSetup",
  async (form: CreateNineSetupForm, { rejectWithValue }) => {
    try {
      return await NineSetupApiService.createNineSetup(form);
    } catch (error) {
      return rejectWithValue(toFriendlyNineSetupError(error));
    }
  }
);

/**
 * Partial update — also the way status transitions are performed. Resolves to
 * the server's version of the row (with the lifecycle timestamp it stamped),
 * which the slice swaps in wholesale rather than merging the local edit.
 */
export const updateNineSetup = createAsyncThunk(
  "nineSetup/updateNineSetup",
  async (payload: UpdateNineSetupPayload, { rejectWithValue }) => {
    try {
      return await NineSetupApiService.updateNineSetup(payload);
    } catch (error) {
      return rejectWithValue(toFriendlyNineSetupError(error));
    }
  }
);

/**
 * Loads the shared operator queue — every `approved` row waiting for media collection.
 */

/**
 * Admin enquiry update (enquiryNote, enquiryTargetDate).
 */
export const updateNineSetupEnquiry = createAsyncThunk(
  "nineSetup/updateNineSetupEnquiry",
  async (payload: UpdateNineSetupEnquiryPayload, { rejectWithValue }) => {
    try {
      return await NineSetupApiService.updateNineSetupEnquiry(payload);
    } catch (error) {
      return rejectWithValue(toFriendlyNineSetupError(error));
    }
  }
);

export const fetchOperatorQueue = createAsyncThunk(
  "nineSetup/fetchOperatorQueue",
  async (_: void, { rejectWithValue }) => {
    try {
      return await NineSetupApiService.getNineSetupsByStatus("approved");
    } catch (error) {
      return rejectWithValue(toFriendlyNineSetupError(error));
    }
  }
);

/**
 * Loads the shared curation queue — every `media_collected` row ready for curators to claim.
 */
export const fetchCuratorQueue = createAsyncThunk(
  "nineSetup/fetchCuratorQueue",
  async (_: void, { rejectWithValue }) => {
    try {
      return await NineSetupApiService.getNineSetupsByStatus("media_collected");
    } catch (error) {
      return rejectWithValue(toFriendlyNineSetupError(error));
    }
  }
);

/** Backwards-compatible alias for fetchCuratorQueue */
export const fetchApprovedQueue = fetchCuratorQueue;

/** Rows this curator has already claimed — their own workload. */
export const fetchCuratorSetups = createAsyncThunk(
  "nineSetup/fetchCuratorSetups",
  async (_: void, { rejectWithValue }) => {
    try {
      return await NineSetupApiService.getCuratorSetups();
    } catch (error) {
      return rejectWithValue(toFriendlyNineSetupError(error));
    }
  }
);

/** A lead's team's rows — the source of the lead's pending earnings. */
export const fetchTeamSetups = createAsyncThunk(
  "nineSetup/fetchTeamSetups",
  async (_: void, { rejectWithValue }) => {
    try {
      return await NineSetupApiService.getTeamSetups();
    } catch (error) {
      return rejectWithValue(toFriendlyNineSetupError(error));
    }
  }
);

/**
 * Hard-deletes a row and resolves to its id so the slice can drop it from
 * state. For "this didn't work out", set `status` to rejected/shelved via
 * updateNineSetup instead — that keeps the funnel history intact.
 */
export const deleteNineSetup = createAsyncThunk(
  "nineSetup/deleteNineSetup",
  async (setupId: string, { rejectWithValue }) => {
    try {
      return await NineSetupApiService.deleteNineSetup(setupId);
    } catch (error) {
      return rejectWithValue(toFriendlyNineSetupError(error));
    }
  }
);

/**
 * Loads the admin Onboarding Calendar report for a date range + status. The
 * slice tracks this call's requestId so a slow, superseded response can never
 * overwrite a newer one (see NineSetupDb's calendar cases).
 */
export const fetchCalendarData = createAsyncThunk(
  "nineSetup/fetchCalendarData",
  async (filters: CalendarFilters, { rejectWithValue }) => {
    try {
      return await NineSetupApiService.getCalendarData(filters);
    } catch (error) {
      return rejectWithValue(toFriendlyNineSetupError(error));
    }
  }
);

/**
 * Loads a date range of the calendar report, ALL statuses, for the Onboarding
 * Insights views (targets, tracker) when the calendar's month doesn't cover it. Kept apart from
 * fetchCalendarData so it never replaces the calendar's month data.
 */
export const fetchReportRange = createAsyncThunk<
  CalendarData,
  ReportRangeQuery,
  { rejectValue: string }
>(
  "nineSetup/fetchReportRange",
  async ({ from, to }, { rejectWithValue }) => {
    try {
      return await NineSetupApiService.getCalendarData({ from, to });
    } catch (error) {
      return rejectWithValue(toFriendlyNineSetupError(error));
    }
  }
);

const NineSetupThunk = {
  fetchNineSetups,
  fetchOperatorQueue,
  fetchCuratorQueue,
  fetchApprovedQueue,
  fetchCuratorSetups,
  fetchTeamSetups,
  createNineSetup,
  updateNineSetup,
  deleteNineSetup,
  fetchCalendarData,
  fetchReportRange,
};

export default NineSetupThunk;
